import './BackgroundVideo.css';
import { FC, useEffect, useRef, useState } from 'react';
import useAudioStore from 'stores/audioStore';

const BackgroundVideo: FC<{ muted?: boolean }> = ({ muted = false }) => {
  const { masterVolume } = useAudioStore();
  const [muteVideo, setMuteVideo] = useState<boolean>(muted);
  const bgVideo = useRef<HTMLVideoElement>(null);

  // Loop fix (skip black frame at end)
  useEffect(() => {
    if (!bgVideo.current) return;
    const handler = function (this: HTMLVideoElement) {
      if (this.currentTime >= this.duration - 0.5) this.currentTime = 0.55;
    };
    bgVideo.current.addEventListener('timeupdate', handler);
    return () => {
      bgVideo.current?.removeEventListener('timeupdate', handler);
    };
  }, []);

  // React to masterVolume changes
  useEffect(() => {
    if (masterVolume === 0) {
      setMuteVideo(true);
      if (bgVideo.current) bgVideo.current.muted = true;
    } else {
      setMuteVideo(false);
      if (bgVideo.current) bgVideo.current.muted = false;
    }
  }, [masterVolume]);

  // Unlock sound on first user gesture
  useEffect(() => {
    const removeListeners = () => {
      document.removeEventListener('click', unmuteOnUserGesture);
      document.removeEventListener('keydown', unmuteOnUserGesture);
      document.removeEventListener('touchstart', unmuteOnUserGesture);
      document.removeEventListener('touchmove', unmuteOnUserGesture);
    };

    const unmuteOnUserGesture = () => {
      // console.log('gesture', { mutedProp: muted, video: !!bgVideo.current, masterVolume });
      if (!muted && bgVideo.current && masterVolume > 0) {
        bgVideo.current.muted = false;
        setMuteVideo(false);
        const playPromise = bgVideo.current.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => removeListeners())
            .catch((err) => {
              console.log(err);
            });
        } else {
          removeListeners();
        }
      }
    };

    const passive = { passive: true } as AddEventListenerOptions;
    document.addEventListener('click', unmuteOnUserGesture);
    document.addEventListener('keydown', unmuteOnUserGesture);
    document.addEventListener('touchstart', unmuteOnUserGesture, passive);
    document.addEventListener('touchmove', unmuteOnUserGesture, passive);

    return () => {
      removeListeners();
    };
  }, [muted, masterVolume]);

  return (
    <div className="gradient-bg">
      <div className="gradients-container">
        <img
          src="/images/right-anim-shadow.png"
          alt="Girl shadow animation"
          className="girl-shadow"
        />
        <img
          src="/images/left-anim-shadow.png"
          alt="Left bottom shadow animation"
          className="left-bottom-shadow"
        />
      </div>
      <video
        ref={bgVideo}
        className="block absolute top-0 left-0 w-full h-full object-fill opacity-0"
        src={`${process.env.PUBLIC_URL}/video/onboarding-bg-loop.mp4`}
        autoPlay
        muted={muteVideo}
        loop
        playsInline
      />
    </div>
  );
};

export default BackgroundVideo;
